/* Holo Grail — theme.js v3
   Dependency-free. Everything degrades to native behavior without JS. */
(function () {
  'use strict';
  const HG = window.HG || { routes: {}, cartType: 'page', moneyFormat: '€{{amount_with_comma_separator}}' };
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Preload an image, then run apply() — prevents the "flash then swap" jitter */
  function swapImage(imgEl, src, srcset, alt) {
    if (!imgEl || !src || imgEl.currentSrc === src || imgEl.dataset.pending === src) return;
    imgEl.dataset.pending = src;
    const pre = new Image();
    if (srcset) pre.srcset = srcset;
    if (imgEl.sizes) pre.sizes = imgEl.sizes;
    pre.src = src;
    const apply = () => {
      if (imgEl.dataset.pending !== src) return; /* superseded by a newer swap */
      imgEl.srcset = srcset || '';
      imgEl.src = src;
      if (alt !== undefined) imgEl.alt = alt;
      delete imgEl.dataset.pending;
    };
    if (pre.decode) pre.decode().then(apply).catch(apply);
    else if (pre.complete) apply();
    else { pre.onload = apply; pre.onerror = apply; }
  }

  function formatMoney(cents) {
    const value = (cents / 100);
    const fmt = HG.moneyFormat || '€{{amount}}';
    const map = {
      amount: value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      amount_no_decimals: Math.round(value).toLocaleString('en-US'),
      amount_with_comma_separator: value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      amount_no_decimals_with_comma_separator: Math.round(value).toLocaleString('de-DE')
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => map[key] ?? map.amount);
  }

  /* ---------- Drawers (scrollbar compensation: no layout shift) ---------- */
  const drawers = {
    _lock() {
      /* scrollbar-gutter in CSS reserves the gutter, so padding compensation would shift centred content */
      document.documentElement.classList.add('drawer-open');
      document.body.style.overflow = 'hidden';
    },
    _unlock() {
      document.documentElement.classList.remove('drawer-open');
      document.body.style.overflow = '';
    },
    open(id, trigger) {
      const d = document.getElementById(id);
      if (!d || d.classList.contains('is-open')) return;
      d.removeAttribute('hidden');
      requestAnimationFrame(() => d.classList.add('is-open'));
      drawers._lock();
      d._trigger = trigger || document.activeElement;
      const focusable = d.querySelector('[data-autofocus], input:not([type="hidden"]), a[href], button:not([data-drawer-close])');
      window.setTimeout(() => (focusable || d).focus({ preventScroll: true }), 120);
      d.addEventListener('keydown', drawers._esc);
    },
    close(d) {
      if (typeof d === 'string') d = document.getElementById(d);
      if (!d || !d.classList.contains('is-open')) return;
      d.classList.remove('is-open');
      drawers._unlock();
      d.removeEventListener('keydown', drawers._esc);
      window.setTimeout(() => d.setAttribute('hidden', ''), 350);
      if (d._trigger) d._trigger.focus({ preventScroll: true });
    },
    _esc(e) { if (e.key === 'Escape') drawers.close(e.currentTarget); }
  };
  window.HGDrawers = drawers;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); drawers.open(opener.dataset.drawerOpen, opener); return; }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { e.preventDefault(); drawers.close(closer.closest('.drawer')); }
  });

  /* ---------- Header: scrolled state + ONE search bar shared between hero and header.
     The hero's <predictive-search> element (input, state, open results and all)
     physically moves into the header when you scroll past it, and back again. ---------- */
  function initHeader() {
    const wrap = document.querySelector('.header-wrap');
    const headerSearch = document.querySelector('[data-header-search]');
    const heroSlot = document.querySelector('[data-search-hero-anchor]');
    const heroBar = heroSlot ? heroSlot.querySelector('predictive-search') : null;
    const desktop = window.matchMedia('(min-width: 750px)');
    let folded = false;

    if (headerSearch && heroBar) {
      /* one source of truth: drop the header's own pre-rendered bar */
      headerSearch.querySelector('predictive-search')?.remove();
      headerSearch.classList.add('header__search--collapsed');
      /* reserve the hero slot's height so removing the bar never shifts layout
         (a collapsing hero flips the fold condition and causes oscillation) */
      heroSlot.style.minHeight = heroSlot.offsetHeight + 'px';
    }

    const setFolded = (f) => {
      if (!headerSearch || !heroBar || f === folded) return;
      folded = f;
      const input = heroBar.querySelector('input');
      const hadFocus = heroBar.contains(document.activeElement);
      const caret = input ? [input.selectionStart, input.selectionEnd] : null;
      if (f) {
        heroBar.classList.remove('search-bar--hero');
        headerSearch.appendChild(heroBar);
      } else {
        heroBar.classList.add('search-bar--hero');
        heroSlot.appendChild(heroBar);
      }
      headerSearch.classList.toggle('header__search--collapsed', !f);
      /* Re-fire an input event so the dropdown, if open, re-renders in the new
         position at the correct width instead of carrying stale geometry. */
      if (hadFocus && input) {
        input.focus({ preventScroll: true });
        if (caret) try { input.setSelectionRange(caret[0], caret[1]); } catch (e) {}
      }
    };

    let ticking = false;
    const evaluate = () => {
      ticking = false;
      if (wrap) wrap.classList.toggle('is-scrolled', window.scrollY > 4);
      if (!headerSearch || !heroSlot || !heroBar) return;
      if (!desktop.matches) { setFolded(false); return; }
      const headerH = wrap ? wrap.offsetHeight : 72;
      const bottom = heroSlot.getBoundingClientRect().bottom;
      /* hysteresis so the boundary can't flicker; switches even with results open */
      if (!folded && bottom <= headerH + 4) setFolded(true);
      else if (folded && bottom >= headerH + 48) setFolded(false);
    };
    const onScroll = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(evaluate); } };
    evaluate();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', debounce(evaluate, 150));

    const toggle = document.querySelector('[data-mobile-search-toggle]');
    const panel = document.querySelector('[data-mobile-search]');
    if (toggle && panel) {
      const closePanel = () => { panel.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); };
      toggle.addEventListener('click', () => {
        const open = panel.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open);
        toggle.classList.toggle('is-active', open);
        if (open) window.setTimeout(() => panel.querySelector('input').focus(), 250);
        else closePanel();
      });
      /* close on result tap and on outside click; keep open while interacting with it */
      panel.addEventListener('click', (e) => { if (e.target.closest('.predictive-item, .predictive-results__all')) closePanel(); });
      document.addEventListener('click', (e) => {
        if (panel.classList.contains('is-open') && !panel.contains(e.target) && !toggle.contains(e.target)) closePanel();
      });
    }
  }

  /* ---------- Announcement rotator ---------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-announcement]')];
      if (this.items.length < 2) {
        this.querySelectorAll('[data-ann-nav]').forEach((b) => { b.style.visibility = 'hidden'; });
        return;
      }
      this.index = Math.max(0, this.items.findIndex((el) => el.classList.contains('is-active')));
      this.delay = (parseInt(this.dataset.delay, 10) || 6) * 1000;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ann-nav]');
        if (!btn) return;
        this.go(this.index + (btn.dataset.annNav === 'next' ? 1 : -1));
        this.restart();
      });
      this.addEventListener('mouseenter', () => window.clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.restart());
      this.restart();
    }
    disconnectedCallback() { window.clearInterval(this.timer); }
    restart() {
      window.clearInterval(this.timer);
      if (!reducedMotion.matches) this.timer = window.setInterval(() => this.go(this.index + 1), this.delay);
    }
    go(i) {
      this.index = (i + this.items.length) % this.items.length;
      this.items.forEach((el, n) => el.classList.toggle('is-active', n === this.index));
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._wired) return; /* teleporting between hero and header reconnects us */
      this._wired = true;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.abort = null;
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('focus', () => { if (this.results.innerHTML.trim()) this.show(); });
      this.initRotatingPlaceholder();
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.hide(); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.hide(); });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.hide(); return; }
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      try {
        /* Query the storefront search engine itself (not the suggest API): it
           matches tags, so "black" surfaces products tagged "Black Bolt White
           Flare" — and the dropdown mirrors the search results page exactly. */
        const params = new URLSearchParams({
          q,
          type: 'product',
          'options[prefix]': 'last',
          section_id: 'predictive-search'
        });
        const res = await fetch(`${HG.routes.search}?${params}`, { signal: this.abort.signal });
        if (!res.ok) return;
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const inner = html.querySelector('[data-predictive-inner]');
        if (inner) { this.results.innerHTML = inner.innerHTML; this.show(); }
      } catch (err) { if (err.name !== 'AbortError') console.warn(err); }
    }
    show() { this.results.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
    hide() { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); }
    fill(term) { this.input.value = term; this.input.focus(); this.search(); }
    initRotatingPlaceholder() {
      if (this._placeholderTimer || !this.dataset.rotatingPlaceholders) return;
      const terms = this.dataset.rotatingPlaceholders.split(',').map((t) => t.trim()).filter(Boolean);
      if (terms.length < 2 || !this.input) return;
      const base = this.input.getAttribute('placeholder') || '';
      let i = 0;
      const set = () => {
        if (!this.input.value) this.input.setAttribute('placeholder', terms[i]);
        i = (i + 1) % terms.length;
      };
      set();
      this._placeholderTimer = window.setInterval(set, 5000);
      this.input.addEventListener('blur', () => { if (!this.input.value && base && !terms.includes(base)) this.input.setAttribute('placeholder', base); });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  document.addEventListener('click', (e) => {
    const pill = e.target.closest('[data-search-pill]');
    if (!pill) return;
    const ps = pill.closest('section')?.querySelector('predictive-search') || document.querySelector('predictive-search');
    if (ps) ps.fill(pill.dataset.searchPill);
  });

  /* ---------- Quantity input (respects min/max, disables buttons at bounds) ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button'); if (!btn || btn.disabled) return;
        e.preventDefault();
        const step = btn.dataset.action === 'increase' ? 1 : -1;
        const next = (parseInt(this.input.value, 10) || this.min()) + step;
        const clamped = Math.min(this.max(), Math.max(this.min(), next));
        if (clamped === parseInt(this.input.value, 10)) return;
        this.input.value = clamped;
        this.sync();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.input.addEventListener('input', () => this.sync());
      this.sync();
    }
    min() { return parseInt(this.input.min || 1, 10); }
    max() { return this.input.max ? parseInt(this.input.max, 10) : Infinity; }
    sync() {
      const v = parseInt(this.input.value, 10) || this.min();
      const dec = this.querySelector('[data-action="decrease"]');
      const inc = this.querySelector('[data-action="increase"]');
      if (dec) dec.disabled = v <= this.min();
      if (inc) inc.disabled = v >= this.max();
      this.checkStock(v);
    }
    checkStock(v) {
      /* Block add-to-cart when the requested quantity exceeds available stock. */
      const max = this.max();
      const form = this.closest('form') || document.querySelector('.product__form');
      const submit = form && form.querySelector('[name="add"]');
      const over = Number.isFinite(max) && v > max;
      if (submit && !submit.dataset.lockLabel) {
        submit.dataset.defaultLabel = submit.dataset.defaultLabel || submit.textContent;
      }
      if (submit) {
        submit.classList.toggle('btn--overstock', over);
        submit.disabled = over || submit.dataset.forceDisabled === 'true';
        if (over) submit.textContent = `Nog ${max} beschikbaar`;
        else if (submit.dataset.defaultLabel && submit.textContent.startsWith('Nog ')) submit.textContent = submit.dataset.defaultLabel;
      }
      this.input.setAttribute('aria-invalid', over ? 'true' : 'false');
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart: single round-trip updates, robust when stock runs out ---------- */
  const cart = {
    queue: Promise.resolve(),
    count(n) { document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = n > 0 ? n : ''; }); },
    injectDrawer(sectionHtml) {
      const drawer = document.getElementById('CartDrawer');
      if (!drawer || !sectionHtml) return false;
      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const fresh = html.querySelector('#CartDrawer');
      if (!fresh) return false;
      drawer.querySelector('[data-drawer-content]').innerHTML = fresh.querySelector('[data-drawer-content]').innerHTML;
      drawer.dataset.itemCount = fresh.dataset.itemCount;
      cart.count(parseInt(fresh.dataset.itemCount || '0', 10));
      return true;
    },
    async refreshDrawer() {
      try {
        const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
        if (res.ok) cart.injectDrawer(await res.text());
      } catch (e) { /* leave as-is */ }
    },
    async add(formData, opts = {}) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(HG.routes.cartAdd, { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Kon niet toevoegen');
      cart.injectDrawer(data.sections && data.sections['cart-drawer']);
      if (opts.open !== false) drawers.open('CartDrawer');
      return data;
    },
    change(line, quantity, rowEl) {
      /* Serialize rapid clicks; the drawer always re-syncs, even when Shopify caps the quantity. */
      if (rowEl) rowEl.classList.add('is-busy');
      cart.queue = cart.queue
        .then(async () => {
          const res = await fetch(HG.routes.cartChange, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line, quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
          });
          if (document.querySelector('[data-cart-page]')) { window.location.reload(); return; }
          let injected = false;
          if (res.ok) {
            const data = await res.json();
            injected = cart.injectDrawer(data.sections && data.sections['cart-drawer']);
          }
          if (!injected) await cart.refreshDrawer(); /* e.g. 422 "not enough stock": resync to real state */
        })
        .catch(() => cart.refreshDrawer())
        .finally(() => {
          const row = document.querySelector('.cart-line.is-busy');
          if (row) row.classList.remove('is-busy');
        });
      return cart.queue;
    }
  };

  const stockGuard = {
    async cartQuantities() {
      try {
        const res = await fetch(`${HG.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return {};
        const data = await res.json();
        return (data.items || []).reduce((map, item) => {
          map[item.variant_id] = (map[item.variant_id] || 0) + item.quantity;
          return map;
        }, {});
      } catch (e) { return {}; }
    },
    remaining(variant, inCart) {
      const tracked = variant.inventory_management && variant.inventory_policy !== 'continue';
      if (!tracked) return Infinity;
      return Math.max(0, Number(variant.inventory_quantity || 0) - Number(inCart || 0));
    },
    message(remaining) {
      if (remaining <= 0) return 'Alle beschikbare exemplaren zitten al in je winkelwagen.';
      return remaining === 1 ? 'Er is nog 1 exemplaar beschikbaar.' : `Er zijn nog ${remaining} exemplaren beschikbaar.`;
    },
    showFormError(form, message) {
      const error = form && form.querySelector('[data-cart-stock-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    },
    clearFormError(form) {
      const error = form && form.querySelector('[data-cart-stock-error]');
      if (error) { error.textContent = ''; error.hidden = true; }
    },
    markQuickAdd(btn, message) {
      btn.classList.add('is-unavailable');
      btn.setAttribute('aria-label', message);
      window.clearTimeout(btn._stockResetTimer);
      btn._stockResetTimer = window.setTimeout(() => {
        btn.classList.remove('is-unavailable');
        btn.setAttribute('aria-label', btn.dataset.defaultAria || 'Voeg toe aan winkelwagen');
      }, 1800);
    },
    markSubmitLimit(btn, message) {
      const original = btn.dataset.defaultLabel || btn.textContent.trim();
      btn.textContent = message;
      btn.classList.add('is-stock-limit');
      window.clearTimeout(btn._stockResetTimer);
      btn._stockResetTimer = window.setTimeout(() => {
        btn.textContent = original;
        btn.classList.remove('is-stock-limit');
      }, 2200);
    }
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form || HG.cartType !== 'drawer') return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const label = btn.textContent;
    const fd = new FormData(form);
    const variantId = Number(fd.get('id'));
    const requested = Math.max(1, Number(fd.get('quantity') || 1));
    const picker = document.querySelector('variant-picker');
    const variant = picker?.variants?.find((item) => Number(item.id) === variantId) || {
      id: variantId,
      inventory_management: btn.dataset.inventoryManagement,
      inventory_policy: btn.dataset.inventoryPolicy,
      inventory_quantity: Number(btn.dataset.inventoryQuantity || 0)
    };

    btn.disabled = true;
    stockGuard.clearFormError(form);
    try {
      if (variant) {
        const quantities = await stockGuard.cartQuantities();
        const remaining = stockGuard.remaining(variant, quantities[variantId]);
        if (requested > remaining) {
          stockGuard.markSubmitLimit(
            btn,
            remaining > 0 ? `Nog ${remaining} beschikbaar` : 'Al in winkelwagen'
          );
          return;
        }
      }
      await cart.add(fd);
    } catch (err) {
      stockGuard.showFormError(form, err.message);
    } finally {
      btn.disabled = false;
      if (!btn.classList.contains('is-stock-limit') && !form.querySelector('[data-cart-stock-error]:not([hidden])')) btn.textContent = label;
    }
  });

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.dataset.defaultAria = btn.dataset.defaultAria || btn.getAttribute('aria-label') || '';
    btn.disabled = true;
    const variantId = Number(btn.dataset.quickAdd);
    const tracked = btn.dataset.inventoryManagement && btn.dataset.inventoryPolicy !== 'continue';
    const inventory = Number(btn.dataset.inventoryQuantity || 0);
    const fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', '1');
    try {
      if (tracked) {
        const quantities = await stockGuard.cartQuantities();
        const remaining = Math.max(0, inventory - Number(quantities[variantId] || 0));
        if (remaining < 1) {
          stockGuard.markQuickAdd(btn, stockGuard.message(remaining));
          return;
        }
      }
      if (HG.cartType === 'drawer') {
        await cart.add(fd);
      } else {
        await cart.add(fd, { open: false });
        window.location.href = HG.routes.cart;
        return;
      }
      btn.classList.add('is-added');
      window.setTimeout(() => btn.classList.remove('is-added'), 1500);
    } catch (err) {
      stockGuard.markQuickAdd(btn, err.message);
    } finally {
      btn.disabled = false;
    }
  });

  document.addEventListener('change', (e) => {
    const qty = e.target.closest('[data-cart-qty]');
    if (qty) cart.change(parseInt(qty.dataset.line, 10), parseInt(e.target.value, 10) || 0, qty.closest('.cart-line'));
  });
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-cart-remove]');
    if (rm) { e.preventDefault(); cart.change(parseInt(rm.dataset.line, 10), 0, rm.closest('.cart-line')); }
  });

  /* ---------- Product gallery (preloads before swapping: no flash) ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.img = this.querySelector('[data-gallery-image]');
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      if (!this.img) return;
      this.index = Math.max(0, this.thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true'));
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-gallery-thumb]');
        if (thumb) { e.preventDefault(); this.go(this.thumbs.indexOf(thumb)); return; }
        const nav = e.target.closest('[data-gallery-nav]');
        if (nav) { e.preventDefault(); this.go(this.index + (nav.dataset.galleryNav === 'next' ? 1 : -1)); }
      });
      /* Warm the cache for the next image */
      if (this.thumbs[1]) { const warm = new Image(); warm.src = this.thumbs[1].dataset.src; }

      this.stage = this.querySelector('.product__stage');
      if (this.stage) { this.initLightbox(); this.initSwipe(); }
    }

    initLightbox() {
      const stage = this.stage;
      stage.classList.add('product__stage--zoomable');
      stage.addEventListener('click', (e) => {
        if (e.target.closest('[data-gallery-nav]')) return;
        this.openLightbox();
      });
    }

    openLightbox() {
      const gallery = this;
      const box = document.createElement('div');
      box.className = 'lightbox';
      const multi = this.thumbs.length > 1;
      box.innerHTML =
        '<div class="lightbox__stage"><img alt=""></div>' +
        (multi ? '<button class="lightbox__nav lightbox__nav--prev" type="button" aria-label="Vorige"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="transform:scaleX(-1)"><path d="M4.5 12h15m-5.5-5.5L19.5 12 14 17.5"/></svg></button>' +
                 '<button class="lightbox__nav lightbox__nav--next" type="button" aria-label="Volgende"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 12h15m-5.5-5.5L19.5 12 14 17.5"/></svg></button>' : '') +
        '<button class="lightbox__close" type="button" aria-label="Sluiten">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/></svg> Sluiten</button>';
      document.body.appendChild(box);
      document.body.style.overflow = 'hidden';
      const img = box.querySelector('img');
      const stageEl = box.querySelector('.lightbox__stage');

      const state = { scale: 1, tx: 0, ty: 0 };
      const apply = () => { img.style.transform = `translate(${state.tx}px, ${state.ty}px) scale(${state.scale})`; };
      const reset = () => { state.scale = 1; state.tx = 0; state.ty = 0; img.style.transform = ''; };
      const load = (idx) => {
        reset();
        const t = gallery.thumbs[idx];
        img.src = t ? t.dataset.src : (gallery.img.currentSrc || gallery.img.src);
        img.alt = t ? (t.dataset.alt || '') : (gallery.img.alt || '');
      };
      load(this.index);

      requestAnimationFrame(() => box.classList.add('is-open'));
      const close = () => {
        box.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(() => box.remove(), 250);
        document.removeEventListener('keydown', onKey);
      };
      const onKey = (e) => {
        if (e.key === 'Escape') close();
        else if (multi && e.key === 'ArrowRight') { gallery.go(gallery.index + 1); load(gallery.index); }
        else if (multi && e.key === 'ArrowLeft') { gallery.go(gallery.index - 1); load(gallery.index); }
      };
      document.addEventListener('keydown', onKey);
      box.querySelector('.lightbox__close').addEventListener('click', (e) => { e.stopPropagation(); close(); });
      if (multi) {
        box.querySelector('.lightbox__nav--prev').addEventListener('click', (e) => { e.stopPropagation(); gallery.go(gallery.index - 1); load(gallery.index); });
        box.querySelector('.lightbox__nav--next').addEventListener('click', (e) => { e.stopPropagation(); gallery.go(gallery.index + 1); load(gallery.index); });
      }
      const isTouch = window.matchMedia('(hover: none)').matches;
      if (isTouch) {
        /* mobile: tapping toggles the controls (nav + close) */
        box.classList.add('lightbox--controls');
        stageEl.addEventListener('click', (e) => {
          e.stopPropagation();
          box.classList.toggle('lightbox--controls');
        });
        /* tapping a control shouldn't also toggle */
        box.querySelectorAll('.lightbox__nav, .lightbox__close').forEach((b) =>
          b.addEventListener('click', (e) => e.stopPropagation()));
      } else {
        /* desktop: clicking anywhere closes (including the image); nav handlers
           above already stopPropagation, so arrows navigate without closing.
           Defer binding so the opening click doesn't instantly close it. */
        window.setTimeout(() => box.addEventListener('click', close), 0);
      }

      this.enablePinch(stageEl, img, state, apply);
    }

    enablePinch(stage, img, state, apply) {
      let startDist = 0, startScale = 1, midX = 0, midY = 0;
      let lastX = 0, lastY = 0, mode = null, lastTap = 0;
      const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
      const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
      stage.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          mode = 'pinch';
          startDist = dist(e.touches);
          startScale = state.scale;
          const m = mid(e.touches);
          /* anchor point in image-space so zoom happens toward the pinch centre */
          const r = img.getBoundingClientRect();
          midX = (m.x - r.left - r.width / 2) / state.scale;
          midY = (m.y - r.top - r.height / 2) / state.scale;
        } else if (e.touches.length === 1 && state.scale > 1) {
          mode = 'pan'; lastX = e.touches[0].clientX; lastY = e.touches[0].clientY;
        } else { mode = null; }
      }, { passive: true });
      stage.addEventListener('touchmove', (e) => {
        if (mode === 'pinch' && e.touches.length === 2) {
          e.preventDefault();
          const next = Math.min(4, Math.max(1, startScale * (dist(e.touches) / startDist)));
          const m = mid(e.touches);
          /* keep the pinch midpoint stationary: translate so the anchor stays under the fingers */
          state.tx = m.x - (window.innerWidth / 2) - midX * next;
          state.ty = m.y - (window.innerHeight / 2) - midY * next;
          state.scale = next;
          if (next === 1) { state.tx = 0; state.ty = 0; }
          apply();
        } else if (mode === 'pan' && e.touches.length === 1) {
          e.preventDefault();
          state.tx += e.touches[0].clientX - lastX; state.ty += e.touches[0].clientY - lastY;
          lastX = e.touches[0].clientX; lastY = e.touches[0].clientY;
          apply();
        }
      }, { passive: false });
      stage.addEventListener('touchend', (e) => {
        const now = Date.now();
        if (e.touches.length === 0 && mode === null && now - lastTap < 300) {
          if (state.scale > 1) { state.scale = 1; state.tx = 0; state.ty = 0; }
          else { state.scale = 2.5; }
          apply();
        }
        lastTap = now;
        if (e.touches.length === 0) mode = null;
        else if (e.touches.length === 1 && state.scale > 1) { mode = 'pan'; lastX = e.touches[0].clientX; lastY = e.touches[0].clientY; }
      });
    }

    initSwipe() {
      const stage = this.stage;
      let x0 = null, y0 = null, moved = false;
      stage.addEventListener('touchstart', (e) => {
        if (stage.classList.contains('is-zoomed') || this.thumbs.length < 2) return;
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; moved = false;
      }, { passive: true });
      stage.addEventListener('touchmove', (e) => {
        if (x0 === null) return;
        if (Math.abs(e.touches[0].clientX - x0) > Math.abs(e.touches[0].clientY - y0) + 6) moved = true;
      }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (moved && Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      });
    }
    go(i) {
      if (!this.thumbs.length) return;
      this.index = (i + this.thumbs.length) % this.thumbs.length;
      const t = this.thumbs[this.index];
      this.thumbs.forEach((el, n) => el.setAttribute('aria-current', n === this.index));
      swapImage(this.img, t.dataset.src, t.dataset.srcset, t.dataset.alt || '');
      if (t.closest('.product__thumbs')) {
        t.scrollIntoView({
          block: 'nearest', inline: 'center',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }
      const next = this.thumbs[(this.index + 1) % this.thumbs.length];
      if (next) { const warm = new Image(); warm.src = next.dataset.src; }
    }
    goToMedia(mediaId) {
      const i = this.thumbs.findIndex((t) => t.dataset.mediaId === String(mediaId));
      if (i > -1) this.go(i);
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- Card swatch hover: swap the card image (and keep it) ---------- */
  document.addEventListener('mouseover', (e) => {
    const dot = e.target.closest('.card-swatches [data-swatch-image]');
    if (!dot) return;
    const img = dot.closest('.product-card')?.querySelector('.product-card__media img');
    if (img) swapImage(img, dot.dataset.swatchImage, '');
  });

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.form = document.getElementById(this.dataset.form);
      this.addEventListener('change', () => this.update());
    }
    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value);
    }
    update() {
      const opts = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.querySelectorAll('fieldset').forEach((fs, i) => {
        const label = fs.querySelector('[data-option-value]');
        if (label) label.textContent = opts[i] || '';
      });
      const price = document.querySelector('[data-product-price]');
      const stock = document.querySelector('[data-product-stock]');
      const idInput = this.form?.querySelector('input[name="id"], select[name="id"]');
      const submit = this.form?.querySelector('[name="add"]');
      if (!variant) {
        if (submit) { submit.disabled = true; submit.textContent = HG.strings.soldOut; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (price) {
        price.innerHTML = variant.compare_at_price > variant.price
          ? `<span class="price price--sale"><span class="price__current">${formatMoney(variant.price)}</span> <s class="price__compare">${formatMoney(variant.compare_at_price)}</s></span>`
          : `<span class="price"><span class="price__current">${formatMoney(variant.price)}</span></span>`;
      }
      if (stock) {
        stock.classList.toggle('stock--success', variant.available);
        stock.classList.toggle('stock--out', !variant.available);
        if (variant.available) {
          let html = HG.strings.inStock;
          if (variant.inventory_management && variant.inventory_quantity > 0) {
            html += ` <span class="stock__count">(${variant.inventory_quantity} beschikbaar)</span>`;
          }
          stock.innerHTML = html;
        } else {
          stock.textContent = HG.strings.soldOut;
        }
      }
      if (submit) {
        submit.disabled = !variant.available;
        submit.dataset.forceDisabled = variant.available ? 'false' : 'true';
        submit.textContent = variant.available ? HG.strings.addToCart : HG.strings.soldOut;
        submit.dataset.defaultLabel = variant.available ? HG.strings.addToCart : HG.strings.soldOut;
        submit.dataset.variantId = variant.id;
        submit.dataset.inventoryManagement = variant.inventory_management || '';
        submit.dataset.inventoryPolicy = variant.inventory_policy || '';
        submit.dataset.inventoryQuantity = variant.inventory_quantity ?? 0;
        submit.classList.remove('btn--overstock');
      }
      /* Re-point the quantity selector at THIS variant's inventory and re-check. */
      const qtyEl = document.querySelector('quantity-input');
      if (qtyEl) {
        const qtyInput = qtyEl.querySelector('input');
        if (qtyInput) {
          const tracked = variant.inventory_management && variant.inventory_policy !== 'continue' && variant.inventory_quantity != null;
          if (tracked && variant.inventory_quantity > 0) {
            qtyInput.max = variant.inventory_quantity;
          } else {
            qtyInput.removeAttribute('max');
          }
          /* clamp current value down if it now exceeds stock, then re-run the check */
          const cur = parseInt(qtyInput.value, 10) || 1;
          if (qtyInput.max && cur > parseInt(qtyInput.max, 10)) qtyInput.value = qtyInput.max;
          if (typeof qtyEl.sync === 'function') qtyEl.sync();
        }
      }
      if (variant.featured_media) {
        document.querySelector('product-gallery')?.goToMedia(variant.featured_media.id);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Custom select (styled dropdown, native select stays for the form) ---------- */
  const SELECT_CARET = '<svg class="icon icon--xs" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="M250.0983 79.051 136.0677 193.0816c-4.4542 4.4592 -11.6812 4.4592 -16.1354 0L5.9017 79.051c-6.2105 -6.2104 -3.369 -16.8151 5.1147 -19.0883 3.9373 -1.0549 8.1384 0.0707 11.0207 2.953L128 168.8929 233.9629 62.9157c6.2105 -6.2105 16.8151 -3.369 19.0883 5.1147 1.055 3.9373 -0.0706 8.1383 -2.9529 11.0206Z"></path></svg>';
  class CustomSelect extends HTMLElement {
    connectedCallback() {
      this.native = this.querySelector('select');
      if (!this.native || this.querySelector('.custom-select__btn')) return;
      this.native.classList.add('custom-select__native');
      this.native.tabIndex = -1;
      this.native.setAttribute('aria-hidden', 'true');

      this.btn = document.createElement('button');
      this.btn.type = 'button';
      this.btn.className = 'custom-select__btn';
      this.btn.setAttribute('aria-haspopup', 'listbox');
      this.btn.setAttribute('aria-expanded', 'false');

      this.list = document.createElement('div');
      this.list.className = 'custom-select__list';
      this.list.setAttribute('role', 'listbox');
      this.list.hidden = true;

      [...this.native.options].forEach((opt, i) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'custom-select__option';
        item.setAttribute('role', 'option');
        item.dataset.index = i;
        item.textContent = opt.textContent;
        if (opt.disabled) item.disabled = true;
        this.list.appendChild(item);
      });

      this.append(this.btn, this.list);
      this.syncLabel();

      this.btn.addEventListener('click', () => this.toggle());
      this.list.addEventListener('click', (e) => {
        const item = e.target.closest('.custom-select__option');
        if (!item || item.disabled) return;
        this.native.selectedIndex = parseInt(item.dataset.index, 10);
        this.native.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncLabel();
        this.toggle(false);
        this.btn.focus();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { this.toggle(false); this.btn.focus(); }
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !this.list.hidden) {
          e.preventDefault();
          const items = [...this.list.querySelectorAll('.custom-select__option:not(:disabled)')];
          const current = items.indexOf(document.activeElement);
          const next = e.key === 'ArrowDown' ? Math.min(items.length - 1, current + 1) : Math.max(0, current - 1);
          items[next]?.focus();
        }
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.toggle(false); });
      this.native.addEventListener('change', () => { this.syncLabel(); this.btn.classList.remove('is-invalid'); });
      /* The native select is visually hidden; reroute constraint-validation focus to our button */
      this.native.addEventListener('invalid', (e) => {
        e.preventDefault();
        this.btn.classList.add('is-invalid');
        this.btn.focus();
      });
    }
    syncLabel() {
      const opt = this.native.options[this.native.selectedIndex];
      this.btn.innerHTML = `<span>${opt ? opt.textContent : ''}</span>${SELECT_CARET}`;
      this.list.querySelectorAll('.custom-select__option').forEach((el, i) => {
        el.setAttribute('aria-selected', i === this.native.selectedIndex);
      });
    }
    toggle(force) {
      const open = force !== undefined ? force : this.list.hidden;
      this.list.hidden = !open;
      this.btn.setAttribute('aria-expanded', open);
      if (open) this.list.querySelector('[aria-selected="true"]')?.focus();
    }
  }
  customElements.define('custom-select', CustomSelect);

  /* ---------- Price range slider (dual thumb) ---------- */
  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.rMin = this.querySelector('[data-range-min]');
      this.rMax = this.querySelector('[data-range-max]');
      this.hMin = this.querySelector('[data-input-min]');
      this.hMax = this.querySelector('[data-input-max]');
      this.fill = this.querySelector('.price-range__fill');
      this.lMin = this.querySelector('[data-label-min]');
      this.lMax = this.querySelector('[data-label-max]');
      if (!this.rMin || !this.rMax) return;
      this.bounds = [parseInt(this.rMin.min, 10) || 0, parseInt(this.rMax.max, 10) || 100];
      [this.rMin, this.rMax].forEach((r) => {
        r.addEventListener('input', () => this.paint());
        r.addEventListener('change', () => this.commit());
      });
      this.paint();
    }
    values() {
      let a = parseInt(this.rMin.value, 10), b = parseInt(this.rMax.value, 10);
      if (a > b) { if (document.activeElement === this.rMin) b = a; else a = b; this.rMin.value = a; this.rMax.value = b; }
      return [a, b];
    }
    paint() {
      const [a, b] = this.values();
      const [lo, hi] = this.bounds;
      const pctA = ((a - lo) / (hi - lo)) * 100;
      const pctB = ((b - lo) / (hi - lo)) * 100;
      if (this.fill) { this.fill.style.left = pctA + '%'; this.fill.style.width = (pctB - pctA) + '%'; }
      if (this.lMin) this.lMin.textContent = '€' + a;
      if (this.lMax) this.lMax.textContent = '€' + b;
    }
    commit() {
      const [a, b] = this.values();
      const [lo, hi] = this.bounds;
      if (this.hMin) { this.hMin.disabled = a <= lo; this.hMin.value = a; }
      if (this.hMax) { this.hMax.disabled = b >= hi; this.hMax.value = b; }
      const form = this.closest('form');
      if (form && form.hasAttribute('data-auto-submit')) form.submit();
    }
  }
  customElements.define('price-range', PriceRange);

  /* ---------- Facets auto-submit (checkboxes + custom selects) ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.closest('price-range')) return; /* slider commits itself */
    const form = e.target.closest('form[data-auto-submit]');
    if (form) form.submit();
  });

  /* ---------- Filter groups: open when active, but honour the user's choice ----------
     Default (fresh load): a group is open only if it has an active filter — the
     server sets `open` + data-facet-active="true". The bug we fix: deselecting the
     last value made the group inactive and collapsed it mid-interaction. So we
     remember the user's OWN explicit open/close per group and apply that over the
     default, meaning a group the user opened stays open until they close it. */
  function initFacetState() {
    const KEY = 'hg:facets-user';
    let choice = {};
    try { choice = JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (e) { choice = {}; }
    const save = () => { try { sessionStorage.setItem(KEY, JSON.stringify(choice)); } catch (e) {} };

    document.querySelectorAll('.facets__group[data-facet-key]').forEach((group) => {
      const key = group.dataset.facetKey;
      const isActive = group.dataset.facetActive === 'true';
      /* Apply the user's explicit choice if they've made one; otherwise the server
         default (open iff active) already stands. */
      if (Object.prototype.hasOwnProperty.call(choice, key)) {
        group.open = choice[key] || isActive; /* active groups always stay open */
      }
      group.addEventListener('toggle', () => {
        choice[key] = group.open;
        save();
      });
    });
  }

  /* ---------- Infinite scroll on collection pages ---------- */
  function initInfiniteScroll() {
    const sentinel = document.querySelector('[data-infinite]');
    if (!sentinel || !('IntersectionObserver' in window)) return;
    const pagination = document.querySelector('.pagination');
    if (pagination) pagination.hidden = true;
    let loading = false;
    const io = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting || loading) return;
      loading = true;
      sentinel.classList.add('is-loading');
      try {
        const res = await fetch(sentinel.dataset.nextUrl);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = document.querySelector('[data-product-grid]');
        doc.querySelectorAll('[data-product-grid] > *').forEach((el) => grid.appendChild(el));
        const nextSentinel = doc.querySelector('[data-infinite]');
        if (nextSentinel && nextSentinel.dataset.nextUrl) {
          sentinel.dataset.nextUrl = nextSentinel.dataset.nextUrl;
        } else {
          io.disconnect();
          sentinel.remove();
        }
      } catch (err) {
        io.disconnect();
        sentinel.remove();
        if (pagination) pagination.hidden = false;
      }
      loading = false;
      sentinel.classList.remove('is-loading');
    }, { rootMargin: '600px 0px' });
    io.observe(sentinel);
  }

  /* ---------- Related products via the Recommendations API ---------- */
  async function loadRecommendations() {
    const section = document.querySelector('[data-related-products]');
    if (!section || !section.dataset.url) return;
    try {
      const res = await fetch(section.dataset.url);
      if (!res.ok) return;
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = html.querySelector('[data-related-products]');
      if (fresh && fresh.querySelector('.product-card')) section.innerHTML = fresh.innerHTML;
    } catch (e) { /* keep server-side fallback */ }
  }

  /* ---------- Recently viewed ---------- */
  const RECENT_KEY = 'hg:recent';
  function trackProduct() {
    const el = document.querySelector('[data-recent-product]');
    if (!el) return;
    try {
      const item = JSON.parse(el.textContent);
      let list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      list = [item, ...list.filter((p) => p.handle !== item.handle)].slice(0, 10);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) { /* private mode etc. */ }
  }
  async function renderRecent() {
    const sections = document.querySelectorAll('[data-recently-viewed]');
    if (!sections.length) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) {}
    for (const section of sections) {
      const exclude = section.dataset.exclude;
      const items = list.filter((p) => p.handle !== exclude).slice(0, parseInt(section.dataset.limit || 5, 10));
      const wrapper = section.closest('.shopify-section');
      if (!items.length) { if (wrapper) wrapper.style.display = 'none'; continue; }
      /* Fetch the real product card for each handle via the Section Rendering API,
         so recently viewed uses the exact same card as every other grid. */
      const cards = await Promise.all(items.map(async (p) => {
        try {
          const base = (p.url || `/products/${p.handle}`).split('?')[0];
          const res = await fetch(`${base}?section_id=recent-card`);
          if (!res.ok) return '';
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          return doc.querySelector('[data-recent-card]')?.innerHTML || '';
        } catch (e) { return ''; }
      }));
      const html = cards.filter(Boolean).join('');
      if (!html) { if (wrapper) wrapper.style.display = 'none'; continue; }
      const grid = section.querySelector('[data-recent-grid]');
      grid.innerHTML = html;
    }
  }

  /* ---------- Minimal scroll progress under mobile sliders ---------- */
  function initScrollProgress(root) {
    (root || document).querySelectorAll('.scroll-row-mobile, .product__thumbs').forEach((row) => {
      if (row.dataset.progress) return;
      row.dataset.progress = '1';
      const bar = document.createElement('div');
      bar.className = 'scroll-progress';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span class="scroll-progress__thumb"></span>';
      row.after(bar);
      const thumb = bar.firstElementChild;
      const update = () => {
        const max = row.scrollWidth - row.clientWidth;
        if (max < 8) { bar.style.display = 'none'; return; }
        bar.style.display = '';
        const w = Math.max(16, (row.clientWidth / row.scrollWidth) * 100);
        thumb.style.width = w + '%';
        thumb.style.left = ((row.scrollLeft / max) * (100 - w)) + '%';
      };
      row.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
      /* content arrives async in some sections (recent viewed, recommendations) */
      new MutationObserver(update).observe(row, { childList: true });
    });
  }

  /* ---------- Document pages: build a stable table of contents from the h2s ---------- */
  function initDocToc() {
    document.querySelectorAll('[data-doc]').forEach((doc) => {
      const body = doc.querySelector('[data-doc-body]');
      const tocWrap = doc.querySelector('[data-doc-toc]');
      const list = doc.querySelector('[data-doc-toc-list]');
      if (!body || !list || list.dataset.ready) return;

      const heads = [...body.querySelectorAll('h2')].filter((h) => h.textContent.trim());
      if (heads.length < 2) {
        if (tocWrap) tocWrap.style.display = 'none';
        doc.classList.add('doc-layout--single');
        return;
      }

      list.dataset.ready = '1';
      const usedIds = new Set([...document.querySelectorAll('[id]')].map((el) => el.id));
      const slugify = (text) => text.trim().toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 48) || 'section';

      const links = heads.map((h, i) => {
        if (!h.id) {
          const base = slugify(h.textContent);
          let id = base;
          let n = 2;
          while (usedIds.has(id)) id = `${base}-${n++}`;
          h.id = id;
          usedIds.add(id);
        }

        const a = document.createElement('a');
        a.href = '#' + h.id;
        a.textContent = h.textContent.trim();
        a.addEventListener('click', (e) => {
          e.preventDefault();
          setActive(i);
          const top = h.getBoundingClientRect().top + window.scrollY - getDocScrollOffset();
          window.scrollTo({
            top: Math.max(0, top),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
          history.replaceState(null, '', '#' + h.id);
        });
        list.appendChild(a);
        return a;
      });

      function getDocScrollOffset() {
        const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72;
        return headerH + 24;
      }

      function setActive(index) {
        links.forEach((a, i) => a.classList.toggle('is-active', i === index));
      }

      const updateActive = () => {
        const marker = window.scrollY + getDocScrollOffset() + 2;
        let active = 0;
        heads.forEach((h, i) => {
          const top = h.getBoundingClientRect().top + window.scrollY;
          if (top <= marker) active = i;
        });
        setActive(active);
      };

      window.addEventListener('scroll', updateActive, { passive: true });
      window.addEventListener('resize', debounce(updateActive, 150));
      updateActive();
    });
  }

  /* ---------- Cookie preferences modal: ESC + click-outside to close ---------- */
  function initCookiePrefsUX() {
    const closePrefs = () => {
      const btn = document.querySelector('.shopify-pc__prefs__header-close, [class*="prefs__header-close"]');
      if (btn) { btn.click(); return true; }
      return false;
    };
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const prefs = document.querySelector('#shopify-pc__prefs, [id*="shopify-pc__prefs"]');
      if (prefs && prefs.offsetParent !== null) closePrefs();
    });
    document.addEventListener('click', (e) => {
      /* Shopify wraps the dialog in an overlay; clicking the overlay (not the dialog)
         should dismiss it. */
      const overlay = e.target.closest('.shopify-pc__prefs__overlay, [class*="prefs__overlay"]');
      const dialog = e.target.closest('.shopify-pc__prefs__dialog, [class*="prefs__dialog"]');
      if (overlay && !dialog) closePrefs();
    }, true);
  }

  document.addEventListener('DOMContentLoaded', async () => {
    initCookiePrefsUX();
    initDocToc();
    initHeader();
    initInfiniteScroll();
    initFacetState();
    trackProduct();
    initScrollProgress();
    await Promise.all([loadRecommendations(), renderRecent()]);
    initScrollProgress(); /* rows re-rendered by recommendations get their bar too */
  });
})();